// Enhanced File parser utility for handling various Schwab file formats
// Supports .acc, .rld, .rps, .sec, .tcf, .trn, .uln, .ult, .upn, .upt and CSV formats

import Papa from 'papaparse';

// ============================================================================
// COLUMN DEFINITIONS FOR ALL FILE FORMATS (Based on actual CSV samples)
// ============================================================================

// ACC (Account) file headers - 91 columns based on CSV sample
const ACC_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Account Title Line 1', 'Account Title Line 2', 'Account Title Line 3', 'Account Status',
  'Acct Regis', 'Acct Type', 'Txpyr Title', 'Taxpayer First Name', 'Taxpayer Middle Name',
  'Taxpayer Last Name', 'Txpyr Suff', 'WH CD', 'Primary Contact', 'Alias Name',
  'Mailing Address Line 1', 'Mailing Address Line 2', 'Mailing Address Line 3',
  'Account Mailing City', 'Ac ST', 'Acct Mail Zip', 'Acct Mailing Ctry Code',
  'Email Address', 'Phone NBR', 'Business Ph', 'Date Opened', 'Schwab BR CD',
  'S S', 'Ac Cs', 'Ac Ma', 'M A', 'A R', 'A M', 'Nr AM', 'A O', 'B F', 'Nr Bn',
  'P C', 'P D', 'P B', 'U M', 'F A', 'IntBr CD', 'IntBr MM CD', 'IntBearing Ticker Symbol',
  'IntBearing Feat Is ID', 'Int SF', 'P V', 'P M', 'Statemnt Pref', 'A T', 'Dflt LtSel',
  'C M', 'CBM MF', 'CBM NMF', 'CB Mthd Date', 'VersMrkr #1', 'CustType Code',
  'Organization Primary Name', 'VersMrkr #2', 'Rstr Rsn1', 'Rstr Rsn2', 'Rstr Rsn3',
  'Rstr Rsn4', 'Rstr Rsn5', 'VersMrkr #3', 'Schwab Bank IC Account #', 'VersMrkr #4',
  'SSN/TIN Number', 'VersMrkr #5', 'P E', 'MngdAcct PltfrmCd', 'Managed Account Money Manager',
  'Managed Account Investment Strategy', 'VersMrkr #6', 'Bank Sweep Display Name',
  'VersMrkr #7', 'O L', 'VersMrkr #8', 'Date of Birth', 'Coy CD', 'St CD',
  'VersMrkr #9', 'S I', 'M T', 'Mobile Phone Number', 'col_90', 'col_91'
];

// TRN (Transaction) file headers - 122 columns based on CSV sample  
const TRN_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Account Title Line 1', 'Account Title Line 2', 'Account Title Line 3', 'Acct Regis',
  'Acct Type', 'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ly St', 'Ticker Symbol',
  'Industry Ticker Symbol', 'CUSIP', 'Schwab Sec Nbr', 'Item Issue ID', 'RulSt SufID',
  'ISIN', 'SEDOL', 'Options Display Symbol', 'Underlying Ticker Symbol',
  'Underlying Industry Ticker Symbol', 'Underlyng CUSIP', 'Underly Schwab#',
  'Underlying Itm Iss ID', 'UnRul SufID', 'Underlying ISIN', 'Underly SEDOL',
  'MnyMk Code', 'TR CD', 'SB CD', 'Transaction Category', 'Tran SrCde',
  'Transaction Source Code Description', 'Transaction Detail Description', 'Trade TypCd',
  'T C', 'Trade Date', 'Settlmnt Date', 'Tran Date', 'ExDivdnd Date', 'Quantity',
  'Price', 'Gross Amount', 'Debit Credit', 'Net Amount', 'Commission',
  'Exchange Processing Fee', 'Broker Service Fee', 'Prime Broker Fee', 'Trade Away Fee',
  'Redemption Fee', 'Other Fee', 'Federal TEFRA Withholding', 'State Tax Withholding',
  'St Tx', 'Accrued Interest', 'Accounting Rule Code', 'Ordr SrCd', 'Order Number',
  'Trade Order Entry Tm Stmp', 'Trade Order Exectn Tm Stmp', 'Broker Code', 'Broker Name',
  'SWB From Account', 'SWB To Account', 'S1 Check Number', 'S I', 'Stock Exchg', 'I E',
  'Distribution Rate', 'Cash in Lieu Share Quantity', 'Dividend/Interest Share Quantity',
  'Cash in Lieu Rate', 'Asset Backed Factor', 'Sr Sy', 'Jn Tp', 'D M',
  'Schwab Cashiering Unique ID', 'Recipient Maker Name Line 1', 'Recipient Maker Name Line 2',
  'Recipient Maker Name Line 3', 'Frequency', 'Disburse Check Number',
  'Federal Bank Reference Number', 'Recipient Maker Acct Number', 'Bank Act Typ',
  'Bank Name Part 1', 'Bank Name Part 2', 'Bank ABA#', 'Intermediary Name',
  'Transaction Check Memo 1', 'Transaction Check Memo 2', 'Retirement Fed Income Tax',
  'Retirement State Income Tax', 'Rt Tx', 'Publication Time Stamp', 'VersMrkr #1',
  'TIPS Factor', 'Closing Price', 'VersMrkr #2', 'Transaction Memo', 'VersMrkr #3',
  'Closing Price Unfactored', 'Factor', 'Factor Date', 'VersMrkr #4', 'Step In Fee',
  'MarkupMarkdown', 'Research Fee', 'Exec Brok Comm', 'VersMrkr #5', 'Order ID',
  'Record Date', 'Payable Date', 'AccStart Date', 'AccrEnd Date', 'D F', 'col_121', 'col_122'
];

// ULN (Upload Loan) file headers - 57 columns based on CSV sample
const ULN_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ticker Symbol', 'CUSIP', 'Schwab Sec Nbr',
  'Item Issue ID', 'ISIN', 'SEDOL', 'Options Display Symbol', 'Underlying Ticker Symbol',
  'Underlyng CUSIP', 'Underly Schwab#', 'Underlying Itm Iss ID', 'Underlying ISIN',
  'Underly SEDOL', 'Current Quantity', 'L S', 'Trans Code', 'Current Market Value',
  'Accrued Interest', 'Acquired Date', 'OrgPurch Date', 'Org Purchase Price',
  'Yield toMaturity', 'Cost Basis $ Unamortized', 'Cost Per Share',
  'Adjusted Cost Basis $ Amortized', 'Adjusted Cost Per Share', 'Unrealized Gain/Loss',
  'Days Held', 'H T', 'C B', 'C T', 'A T', 'C F', 'Original Face', 'DFLot Selct',
  'W S', 'VersMrkr #1', 'Disallowed Loss Amount', 'Transaction Cost',
  'Transaction Cost Per Share', 'VersMrkr #2', 'G I', 'Original Cost Basis',
  'VersMrkr #3', 'Adjusted Cost Incldg Unpd Amort', 'VersMrkr #4', 'Event ID',
  'C V', 'col_56', 'col_57'
];

// ULT (Upload Lot Tax) file headers - same as ULN since they use DL records
const ULT_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ticker Symbol', 'CUSIP', 'Schwab Sec Nbr',
  'Item Issue ID', 'ISIN', 'SEDOL', 'Options Display Symbol', 'Underlying Ticker Symbol',
  'Underlyng CUSIP', 'Underly Schwab#', 'Underlying Itm Iss ID', 'Underlying ISIN',
  'Underly SEDOL', 'Current Quantity', 'L S', 'Trans Code', 'Current Market Value',
  'Accrued Interest', 'Acquired Date', 'OrgPurch Date', 'Org Purchase Price',
  'Yield toMaturity', 'Cost Basis $ Unamortized', 'Cost Per Share',
  'Adjusted Cost Basis $ Amortized', 'Adjusted Cost Per Share', 'Unrealized Gain/Loss',
  'Days Held', 'H T', 'C B', 'C T', 'A T', 'C F', 'Original Face', 'DFLot Selct',
  'W S', 'VersMrkr #1', 'Disallowed Loss Amount', 'Transaction Cost',
  'Transaction Cost Per Share', 'VersMrkr #2', 'G I', 'Original Cost Basis',
  'VersMrkr #3', 'Adjusted Cost Incldg Unpd Amort', 'VersMrkr #4', 'Event ID',
  'C V', 'col_56', 'col_57'
];

// RLD (Reconciliation Data) file headers - based on H1 and T1 records
const RLD_HEADERS = [
  'Record Type', 'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 
  'File Type', 'Version', 'Record Count'
];

// TCF (Trade Confirmation) file headers - based on H1 and T1 records  
const TCF_HEADERS = [
  'Record Type', 'Custdian ID', 'Confirmation Type', 'MstrAcct Number', 'Master Account Name', 
  'Business Date', 'Version', 'Record Count'
];

// UPN (Upload Position) file headers - 40 columns based on CSV sample (DP records)
const UPN_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Security Type', 'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ticker Symbol', 'CUSIP',
  'Schwab Sec Nbr', 'Item Issue ID', 'ISIN', 'SEDOL', 'Options Display Symbol',
  'Underlying Ticker Symbol', 'Underlyng CUSIP', 'Underly Schwab#', 'Underlying Itm Iss ID',
  'Underlying ISIN', 'Underly SEDOL', 'Current Quantity', 'L S', 'Current Market Value',
  'Accrued Interest', 'Cost Basis $ Unamortized', 'Cost Per Share',
  'Adjusted Cost Basis $ Amortized', 'Adjusted Cost Per Share', 'Unrealized Gain/Loss',
  'C B', 'C T', 'A T', 'C F', 'Original Face', 'DFLot Selct', 'C M',
  'Principal Paydown Factor', 'col_39', 'col_40'
];

// UPT (Upload Position Tax) file headers - 40 columns based on CSV sample
const UPT_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Security Type', 'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ticker Symbol', 'CUSIP',
  'Schwab Sec Nbr', 'Item Issue ID', 'ISIN', 'SEDOL', 'Options Display Symbol',
  'Underlying Ticker Symbol', 'Underlyng CUSIP', 'Underly Schwab#', 'Underlying Itm Iss ID',
  'Underlying ISIN', 'Underly SEDOL', 'Current Quantity', 'L S', 'Current Market Value',
  'Accrued Interest', 'Cost Basis $ Unamortized', 'Cost Per Share',
  'Adjusted Cost Basis $ Amortized', 'Adjusted Cost Per Share', 'Unrealized Gain/Loss',
  'C B', 'C T', 'A T', 'C F', 'Original Face', 'DFLot Selct', 'C M',
  'Principal Paydown Factor', 'col_39', 'col_40'
];

// RPS (Report Position) file headers - based on D1 records (60 columns)
const RPS_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'Account ID',
  'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ly St', 'Ticker Symbol',
  'Industry Ticker Symbol', 'CUSIP', 'Schwab Sec Nbr', 'Item Issue ID', 'RulSt SufID',
  'ISIN', 'SEDOL', 'Options Display Symbol', 'Security Description Line 1',
  'Security Description Line 2', 'Security Description Line 3', 'ScrtyDes Line 4',
  'Underlying Ticker Symbol', 'Underlying Industry Ticker Symbol', 'Underlyng CUSIP',
  'Underly Schwab#', 'Underlying Itm Iss ID', 'UnRul SufID', 'Underlying ISIN',
  'Underly SEDOL', 'MnyMk Code', 'D R', 'C G', 'Closing Price', 'SecPrice LstUpDte',
  'Quantity Settled/Unsettled', 'L S', 'Market Value Settled/Unsettled', 'Accounting Rule Code',
  'Quantity Settled', 'Quantity Unsettled/Long', 'Quantity Unsettled/Short', 'VersMrkr #1',
  'TIPS Factor', 'Asset Backed Factor', 'VersMrkr #2', 'Closing Price Unfactored',
  'Factor', 'Factor Date', 'VersMrkr #4', 'Non-Tradable Quantity', 'VersMrkr #5', 'A I'
];

// SEC (Securities) file headers - based on D1 records (75 columns)
const SEC_HEADERS = [
  'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date',
  'Prod Code', 'ProdCatg Code', 'Tax Code', 'Ly St', 'Ticker Symbol',
  'Industry Ticker Symbol', 'CUSIP', 'Schwab Sec Nbr', 'ReOrg Sec Nbr', 'Item Issue ID',
  'RulSt SufID', 'ISIN', 'SEDOL', 'Options Display Symbol', 'Security Description Line 1',
  'Security Description Line 2', 'Security Description Line 3', 'ScrtyDes Line 4',
  'Underlying Ticker Symbol', 'Underlying Industry Ticker Symbol', 'Underlyng CUSIP',
  'Underly Schwab#', 'Underlying Itm Iss ID', 'UnRul SufID', 'Underlying ISIN',
  'Underly SEDOL', 'MnyMk Code', 'Last Update', 'S F', 'Closing Price', 'SecPrice LstUpDte',
  'Security Valuation Unit', 'OptnRt Symbol', 'Opt Expr Date', 'C P', 'Strike Price',
  'Interest Rate', 'Maturity Date', 'TIPS Factor', 'Asset Backed Factor', 'Face Value Amt',
  'St Cd', 'VersMrkr #1', 'P I', 'O I', 'VersMrkr #2', 'Closing Price Unfactored',
  'Factor', 'Factor Date', 'VersMrkr #3', 'Call Date', 'Call Price', 'Issue Date',
  '1st Cpn Date', 'Options Multiplier', 'Dividend/Interest Frequency', 'S&P Rating',
  'Moodys Rating', 'Next Pay Date', 'Next Put Date', 'Next Put Price', 'Mkt Code',
  'Par Value', 'Is Cy', 'Cy Is'
];

// Standard CSV headers for simple formats and empty files
const STANDARD_HEADERS = [
  'Date', 'Symbol', 'Quantity', 'Price', 'Amount', 'Type', 'Account', 'Description'
];

// ============================================================================
// FILE TYPE DETECTION AND HEADER MAPPING
// ============================================================================

const getFileTypeInfo = (extension) => {
  const typeMap = {
    'csv': { type: 'csv', headers: STANDARD_HEADERS, description: 'Comma-separated values' },
    'txt': { type: 'text', headers: STANDARD_HEADERS, description: 'Text files (delimited)' },
    'acc': { type: 'acc', headers: ACC_HEADERS, description: 'Account files' },
    'rld': { type: 'rld', headers: RLD_HEADERS, description: 'Reconciliation data files' },
    'rps': { type: 'rps', headers: RPS_HEADERS, description: 'Report position files' },
    'sec': { type: 'sec', headers: SEC_HEADERS, description: 'Securities files' },
    'tcf': { type: 'tcf', headers: TCF_HEADERS, description: 'Trade confirmation files' },
    'trn': { type: 'trn', headers: TRN_HEADERS, description: 'Transaction files' },
    'uln': { type: 'uln', headers: ULN_HEADERS, description: 'Upload loan files' },
    'ult': { type: 'ult', headers: ULT_HEADERS, description: 'Upload lot tax files' },
    'upn': { type: 'upn', headers: UPN_HEADERS, description: 'Upload position files' },
    'upt': { type: 'upt', headers: UPT_HEADERS, description: 'Upload position tax files' }
  };
  
  return typeMap[extension] || { 
    type: 'unknown', 
    headers: STANDARD_HEADERS, 
    description: 'Unknown format' 
  };
};

export const detectFileFormat = (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  const typeInfo = getFileTypeInfo(extension);
  
  return {
    extension,
    type: typeInfo.type,
    headers: typeInfo.headers,
    description: typeInfo.description,
    name: file.name,
    size: file.size
  };
};

// ============================================================================
// MAIN PARSING FUNCTION
// ============================================================================

export const parseFileToCSV = async (file) => {
  try {
    const fileInfo = detectFileFormat(file);
    const content = await readFileContent(file);
    
    let parsedData;
    let headers = fileInfo.headers;
    
    switch (fileInfo.type) {
      case 'csv':
        parsedData = await parseCSV(content);
        break;
      case 'acc':
        parsedData = parseACCFile(content);
        break;
      case 'trn':
        parsedData = parseTRNFile(content);
        break;
      case 'uln':
        parsedData = parseULNFile(content);
        break;
      case 'ult':
        parsedData = parseULTFile(content);
        break;
      case 'upn':
        parsedData = parseUPNFile(content);
        break;
      case 'upt':
        parsedData = parseUPTFile(content);
        break;
      case 'rps':
        parsedData = parseRPSFile(content);
        break;
      case 'sec':
        parsedData = parseSECFile(content);
        break;
      case 'rld':
        parsedData = parseRLDFile(content);
        break;
      case 'tcf':
        parsedData = parseTCFFile(content);
        break;
      default:
        parsedData = parseGenericFile(content);
    }

      return {
        success: true,
        data: parsedData,
        headers: headers,
        originalFormat: fileInfo.extension,
        fileName: fileInfo.name,
        rowCount: parsedData.length,
      fileType: fileInfo.type,
      description: fileInfo.description
    };
    
  } catch (error) {
    return {
      success: false,
      error: error.message,
      fileName: file.name
    };
  }
};

// ============================================================================
// SPECIFIC FILE PARSERS
// ============================================================================

/**
 * Parse ACC (Account) files
 * Format: H1|H2|H3|D1|T1 records with pipe delimiters
 */
const parseACCFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process D1 (Data) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'D1' && parts.length >= 91) {
    const row = {};
      ACC_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (D1)
    });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse TRN (Transaction) files
 * Format: H1|H2|H3|D1|T1 records with pipe delimiters
 */
const parseTRNFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process D1 (Data) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'D1' && parts.length >= 122) {
      const row = {};
      TRN_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (D1)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse ULN (Upload Loan) files
 * Format: H1|H2|H3|DL|T1 records with pipe delimiters
 */
const parseULNFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process DL (Data Loan) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'DL' && parts.length >= 57) {
      const row = {};
      ULN_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (DL)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse ULT (Upload Lot Tax) files
 * Format: H1|H2|H3|DL|T1 records with pipe delimiters (same as ULN)
 */
const parseULTFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process DL (Data Loan) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'DL' && parts.length >= 50) {
      const row = {};
      ULT_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (DL)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse UPN (Upload Position) files
 * Format: Can be either H1|T1 only (old files) or H1|H2|H3|DP|T1 (new files with data)
 */
const parseUPNFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  let hasDataRecords = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // First check if file has DP records (new format)
    if (parts.length > 0 && parts[0] === 'DP' && parts.length >= 39) {
      hasDataRecords = true;
      const row = {};
      UPN_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (DP)
      });
      data.push(row);
    }
  }
  
  // If no DP records found, treat as old format with H1/T1 records as data
  if (!hasDataRecords) {
    const simpleHeaders = ['Record Type', 'Custdian ID', 'MstrAcct Number', 'Master Account Name', 'Business Date', 'File Type', 'Version', 'Record Count'];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const parts = line.split('|');
      
      // Process H1 and T1 records as data for old UPN files
      if (parts.length > 0 && (parts[0] === 'H1' || parts[0] === 'T1')) {
        const row = {};
        simpleHeaders.forEach((header, index) => {
          if (index === 0) {
            row[header] = parts[0] || '';
    } else {
            row[header] = parts[index] || '';
          }
        });
        data.push(row);
      }
    }
  }
  
  return data;
};

/**
 * Parse UPT (Upload Position Tax) files
 * Format: H1|H2|H3|DP|T1 records with pipe delimiters
 */
const parseUPTFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process DP (Data Position) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'DP' && parts.length >= 39) {
      const row = {};
      UPT_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (DP)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse RPS (Report Position) files
 * Format: H1|H2|H3|D1|D2|T1 records with pipe delimiters
 */
const parseRPSFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process D1 (Data Position) records - skip H1, H2, H3, H4, H5, D2, T1
    if (parts.length > 0 && parts[0] === 'D1' && parts.length >= 50) {
      const row = {};
      RPS_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (D1)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse SEC (Securities) files
 * Format: H1|H2|H3|D1|T1 records with pipe delimiters
 */
const parseSECFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Only process D1 (Data) records - skip H1, H2, H3, T1
    if (parts.length > 0 && parts[0] === 'D1' && parts.length >= 70) {
      const row = {};
      SEC_HEADERS.forEach((header, index) => {
        row[header] = parts[index + 1] || ''; // Skip first part (D1)
      });
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse RLD (Reconciliation Data) files
 * Format: H1|T1 records - these ARE the data records for RLD files
 */
const parseRLDFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Process both H1 and T1 records as data for RLD files
    if (parts.length > 0 && (parts[0] === 'H1' || parts[0] === 'T1')) {
      const row = {
        'Record Type': parts[0] || '',
        'Custdian ID': parts[1] || '',
        'MstrAcct Number': parts[2] || '',
        'Master Account Name': parts[3] || '',
        'Business Date': parts[4] || '',
        'File Type': parts[5] || '',
        'Version': parts[6] || '',
        'Record Count': parts[7] || ''
      };
      data.push(row);
    }
  }
  
  return data;
};

/**
 * Parse TCF (Trade Confirmation) files
 * Format: H1|T1 records - these ARE the data records for TCF files
 */
const parseTCFFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  const data = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const parts = line.split('|');
    
    // Process both H1 and T1 records as data for TCF files
    if (parts.length > 0 && (parts[0] === 'H1' || parts[0] === 'T1')) {
      const row = {
        'Record Type': parts[0] || '',
        'Custdian ID': parts[1] || '',
        'Confirmation Type': parts[2] || '',
        'MstrAcct Number': parts[3] || '',
        'Master Account Name': parts[4] || '',
        'Business Date': parts[5] || '',
        'Version': parts[6] || '',
        'Record Count': parts[7] || ''
      };
      data.push(row);
    }
  }
  
  return data;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const readFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const parseCSV = (content) => {
  return new Promise((resolve, reject) => {
    Papa.parse(content, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
  } else {
          resolve(results.data);
        }
      },
      error: (error) => reject(new Error(`CSV parsing failed: ${error.message}`))
    });
  });
};

const parseGenericFile = (content) => {
  const lines = content.split('\n').filter(line => line.trim());
  if (lines.length === 0) throw new Error('File is empty');
  
  let delimiter = '\t';
  const firstLine = lines[0];
  
  if (firstLine.includes(',')) delimiter = ',';
  else if (firstLine.includes(';')) delimiter = ';';
  else if (firstLine.includes('|')) delimiter = '|';
  
  const headers = firstLine.split(delimiter)?.map(h => h.trim());
  const data = lines.slice(1)?.map(line => {
    const values = line.split(delimiter)?.map(v => v.trim());
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    return row;
  });
  
  return data;
};

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

export const convertToCSV = (data, headers = STANDARD_HEADERS) => {
  if (!data || data.length === 0) {
    return headers.join(',') + '\n';
  }
  
  const csvRows = [headers.join(',')];
  
  data.forEach(row => {
    const values = headers?.map(header => {
      const value = row[header] || '';
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
};

export const downloadCSV = (data, filename = 'converted_data.csv') => {
  const csv = convertToCSV(data);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const validateData = (data) => {
  const errors = [];
  const warnings = [];
  
  if (!data || data.length === 0) {
    // For empty files, this is not necessarily an error
    warnings.push('File contains no data records (this may be normal for certain file types)');
    return { isValid: true, errors, warnings };
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const getSupportedFormats = () => {
  return [
    { ext: 'CSV', columns: 8, description: 'Comma-separated values', color: 'success' },
    { ext: 'ACC', columns: 91, description: 'Account files', color: 'info' },
    { ext: 'TRN', columns: 122, description: 'Transaction files', color: 'primary' },
    { ext: 'ULN', columns: 57, description: 'Upload loan files', color: 'warning' },
    { ext: 'ULT', columns: 57, description: 'Upload lot tax files', color: 'warning' },
    { ext: 'UPN', columns: 40, description: 'Upload position files', color: 'secondary' },
    { ext: 'UPT', columns: 40, description: 'Upload position tax files', color: 'primary' },
    { ext: 'RPS', columns: 50, description: 'Report position files', color: 'info' },
    { ext: 'SEC', columns: 70, description: 'Securities files', color: 'warning' },
    { ext: 'RLD', columns: 8, description: 'Reconciliation data files', color: 'default' },
    { ext: 'TCF', columns: 8, description: 'Trade confirmation files', color: 'default' }
  ];
};
